/** Copyright 2025 Midas Healthcare Solutions - All Rights Reserved **/
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, forwardRef, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ApiService, Organization } from '@midas/shared/common';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { map, Observable } from 'rxjs';
import { IQuerySingle } from '../ui-query-single';
import { PagedResult } from '../ui-query-select';

@Component({
  selector: 'midas-ui-organization-query-single',
  templateUrl: './ui-organization-query-single.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    TranslocoModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiOrganizationQuerySingleComponent),
      multi: true,
    },
  ],
})
export class UiOrganizationQuerySingleComponent extends IQuerySingle<Organization> {
  @Input() getByName = false;

  constructor(
    private api: ApiService,
    private transloco: TranslocoService,
    protected _change: ChangeDetectorRef,
  ) {
    super(_change);
  }

  override getValue(item: Organization) {
    return this.getByName ? item.name : item.id;
  }

  getItemsBySearchString(searchString?: string | null): Observable<PagedResult<Organization>> {
    return this.api
      .get<{ count: number; organizations: Organization[] }>('/organization', {
        startingIndex: 0,
        searchString: searchString,
      })
      .pipe(
        map((res) => ({
          count: res.organizations.length,
          items: res.organizations.sort((a, b) => a.name.localeCompare(b.name)),
        })),
      );
  }

  getItemById(id: number): Observable<Organization | null> {
    return this.api
      .get<{ count: number; organizations: Organization[] }>('/organization', { startingIndex: 0 })
      .pipe(map((res) => res.organizations.find((o) => o.id === id) ?? null));
  }

  getDisplayName(item: Organization): string {
    return item.name || '';
  }

  getPlaceholderText(): string {
    return this.transloco.translate('Search Organizations');
  }
}
